/**
 * AI assistant tab on the order/offer detail page. Chat UI cloned from the
 * chat-inbox detail view (bubbles, reply bar, WS + poll fallback), plus
 * proposal cards: every AI edit arrives as a preview with Apply / Reject —
 * nothing changes on the order until Apply succeeds.
 */
define('ordersDetailAiChatView',[
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'notificationCenter',
  'compuzzProUtils',
  'entities/ai_chat'
], function (Module, App, Marionette, _, $, Settings, NotificationCenter, CompuzzProUtils) {
  'use strict';

  function escapeHtml(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/\n/g, '<br/>');
  }

  function escapeAttr(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Last model the user picked, remembered across sessions/orders (per
  // browser). localStorage can be unavailable (private mode) — degrade to
  // the per-session default silently.
  var MODEL_STORAGE_KEY = 'compuzz-ai-chat-model';

  function storedModelCode() {
    try {
      return window.localStorage.getItem(MODEL_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function storeModelCode(code) {
    try {
      window.localStorage.setItem(MODEL_STORAGE_KEY, code);
    } catch (e) {
      // best effort only
    }
  }

  function formatTokens(count) {
    if (count === null || count === undefined) { return '0'; }
    if (count >= 1000000) { return (count / 1000000).toFixed(1) + 'M'; }
    if (count >= 1000) { return (count / 1000).toFixed(1) + 'k'; }
    return String(count);
  }

  // Per-turn usage chip on assistant messages: the cost when the model entry
  // has prices configured, the token count otherwise; details in the tooltip.
  function usageChip(msg) {
    if (msg.sender !== 'ASSISTANT' ||
        (msg.inputTokens === null || msg.inputTokens === undefined)) { return ''; }
    var tooltip = formatTokens(msg.inputTokens) + ' in (' +
      formatTokens(msg.cacheReadTokens) + ' cached) / ' +
      formatTokens(msg.outputTokens) + ' out';
    var label;
    if (msg.costAmount !== null && msg.costAmount !== undefined) {
      var cost = Number(msg.costAmount);
      label = cost < 0.01 ? '< 0.01' : cost.toFixed(2);
    } else {
      label = formatTokens((msg.inputTokens || 0) + (msg.outputTokens || 0)) + ' tok';
    }
    return '<span class="usage-chip" title="' + escapeAttr(tooltip) + '">' +
      escapeHtml(label) + '</span>';
  }

  function relativeTime(date) {
    if (!date) { return ''; }
    var diff = Date.now() - date.getTime();
    var min = Math.round(diff / 60000);
    if (min < 1) { return _.i18n('common.justNow', null, 'just now'); }
    if (min < 60) { return min + ' min'; }
    var hr = Math.round(min / 60);
    if (hr < 24) { return hr + ' h'; }
    return Math.round(hr / 24) + ' d';
  }

  function statusChip(status) {
    var label;
    switch (status) {
      case 'PROPOSED': label = _.i18n('orders.aiAssistant.status.proposed', null, 'Awaiting approval'); break;
      case 'APPLIED': label = _.i18n('orders.aiAssistant.status.applied', null, 'Applied'); break;
      case 'REJECTED': label = _.i18n('orders.aiAssistant.status.rejected', null, 'Rejected'); break;
      case 'SUPERSEDED': label = _.i18n('orders.aiAssistant.status.superseded', null, 'Outdated'); break;
      case 'FAILED': label = _.i18n('orders.aiAssistant.status.failed', null, 'Failed'); break;
      default: label = status;
    }
    return '<span class="job-status s-' + escapeAttr(status) + '">' + escapeHtml(label) + '</span>';
  }

  function renderJobCard(job, orderValidated) {
    var canDecide = job.status === 'PROPOSED' && !orderValidated;
    var html = '' +
      '<div class="ai-job-card" data-job-sec-id="' + escapeAttr(job.secId) + '">' +
      '  <div class="job-head">' +
      statusChip(job.status) +
      (job.summary ? '<span class="job-summary">' + escapeHtml(job.summary) + '</span>' : '') +
      '  </div>' +
      (job.previewUrl ?
        '  <a class="job-preview" href="' + escapeAttr(job.previewUrl) + '" target="_blank" rel="noopener">' +
        '    <img src="' + escapeAttr(job.previewUrl) + '" alt="preview"/>' +
        '  </a>' : '') +
      (job.errorMessage ? '<div class="job-error">' + escapeHtml(job.errorMessage) + '</div>' : '');
    if (canDecide) {
      html += '' +
        '  <div class="job-actions">' +
        '    <button class="btn btn-apply">' + (_.i18n('orders.aiAssistant.apply', null, 'Apply')) + '</button>' +
        '    <button class="btn btn-reject">' + (_.i18n('orders.aiAssistant.reject', null, 'Reject')) + '</button>' +
        '  </div>';
    } else if (job.status === 'PROPOSED' && orderValidated) {
      html += '<div class="job-locked">' +
        (_.i18n('orders.aiAssistant.orderValidated', null, 'Order is validated — edits are locked.')) +
        '</div>';
    }
    html += '</div>';
    return html;
  }

  // Approval card of a mutating API call (call_api proposal). Reuses the
  // edit-job card classes — only the data attribute differs, so the click
  // handlers can route to the action endpoints. The backend re-validates on
  // Apply, so no client-side lock is needed here.
  function renderActionJobCard(job) {
    var summary = job.summary || ((job.httpMethod || '') + ' ' + (job.path || ''));
    var html = '' +
      '<div class="ai-job-card ai-action-card" data-action-sec-id="' + escapeAttr(job.secId) + '">' +
      '  <div class="job-head">' +
      statusChip(job.status) +
      '<span class="job-summary">' + escapeHtml(summary) + '</span>' +
      '  </div>' +
      (job.errorMessage ? '<div class="job-error">' + escapeHtml(job.errorMessage) + '</div>' : '');
    if (job.status === 'PROPOSED') {
      html += '' +
        '  <div class="job-actions">' +
        '    <button class="btn btn-apply">' + (_.i18n('orders.aiAssistant.apply', null, 'Apply')) + '</button>' +
        '    <button class="btn btn-reject">' + (_.i18n('orders.aiAssistant.reject', null, 'Reject')) + '</button>' +
        '  </div>';
    }
    html += '</div>';
    return html;
  }

  function renderMessage(msg, orderValidated) {
    var sender = msg.sender || 'SYSTEM';
    var side = sender === 'USER' ? 'outgoing' : 'incoming';
    var senderLabel;
    switch (sender) {
      case 'USER': senderLabel = msg.userpidName || (_.i18n('common.you', null, 'You')); break;
      case 'ASSISTANT': senderLabel = _.i18n('orders.aiAssistant.name', null, 'AI assistant'); break;
      default: senderLabel = _.i18n('common.system', null, 'System');
    }
    var when = msg.sentAt ? new Date(msg.sentAt) : null;
    var fileHtml = '';
    if (msg.fileUrl) {
      var isImage = /\.(png|jpe?g|gif|bmp|webp)$/i.test(msg.fileName || '');
      if (isImage) {
        fileHtml = '<a class="msg-file msg-file-image" href="' + escapeAttr(msg.fileUrl) +
          '" target="_blank" rel="noopener" title="' + escapeAttr(msg.fileName || '') + '">' +
          '<img src="' + escapeAttr(msg.fileUrl) + '" alt="' + escapeAttr(msg.fileName || 'file') + '"/>' +
          '</a>';
      } else {
        fileHtml = '<a class="msg-file" href="' + escapeAttr(msg.fileUrl) +
          '" target="_blank" rel="noopener">&#128206; ' + escapeHtml(msg.fileName || 'file') + '</a>';
      }
    }
    var jobsHtml = _.map(msg.editJobs || [], function (job) {
      return renderJobCard(job, orderValidated);
    }).join('');
    jobsHtml += _.map(msg.actionJobs || [], function (job) {
      return renderActionJobCard(job);
    }).join('');
    var excluded = !!msg.excludedFromContext;
    var contextToggle = '';
    if (msg.secId) {
      contextToggle = '<button class="msg-context-toggle" data-message-sec-id="' +
        escapeAttr(msg.secId) + '" data-included="' + (excluded ? '0' : '1') + '" title="' +
        escapeAttr(excluded ?
          _.i18n('orders.aiAssistant.includeMessage', null, 'Restore into the AI context') :
          _.i18n('orders.aiAssistant.excludeMessage', null, 'Remove from the AI context')) +
        '">' + (excluded ? '&#8634;' : '&times;') + '</button>';
    }
    return '' +
      '<li class="msg msg-' + side + ' sender-' + sender + (excluded ? ' msg-excluded' : '') + '">' +
      '  <div class="bubble' + (jobsHtml ? ' bubble-wide' : '') + '">' +
      '    <div class="meta"><span class="who">' + escapeHtml(senderLabel) + '</span>' +
      (when ? ' <span class="when" title="' + escapeHtml(when.toLocaleString()) + '">' +
        escapeHtml(relativeTime(when)) + '</span>' : '') +
      usageChip(msg) +
      contextToggle +
      (excluded ? '<span class="excluded-note">' +
        escapeHtml(_.i18n('orders.aiAssistant.excludedNote', null, 'not sent to the AI')) +
        '</span>' : '') +
      '    </div>' +
      (msg.text ? '    <div class="text">' + escapeHtml(msg.text) + '</div>' : '') +
      fileHtml +
      jobsHtml +
      '  </div>' +
      '</li>';
  }

  Module.exports = Marionette.LayoutView.extend({
    className: 'orders-detail-ai-chat-view',

    template: _.template(
      '<% if (attachmentUrl) { %>' +
      '<div class="attachment-original">' +
      '  <img class="attachment-original-img" src="<%- attachmentUrl %>" ' +
      'alt="<%- attachmentName %>" title="<%- zoomTitle %>"/>' +
      '  <div class="attachment-original-meta">' +
      '    <div class="name"><%- attachmentName %></div>' +
      '    <div class="hint"><%= originalHint %> ' +
      '      <a href="<%- attachmentUrl %>" target="_blank" rel="noopener"><%= openLabel %></a>' +
      '    </div>' +
      '  </div>' +
      '</div>' +
      '<div class="img-zoom-overlay" style="display:none;"><img/></div>' +
      '<% } %>' +
      '<div class="thread-panel">' +
      '  <ul class="msg-list"></ul>' +
      '  <div class="thinking" style="display:none;">' +
      '    <div class="bubble">' +
      '      <div class="meta"><span class="who"><%= assistantName %></span> ' +
      '        <span class="thinking-elapsed"></span></div>' +
      '      <span class="dots"><span></span><span></span><span></span></span> ' +
      '      <span class="thinking-status"><%= thinkingLabel %></span>' +
      '    </div>' +
      '  </div>' +
      '</div>' +
      '<div class="reply-bar">' +
      '  <textarea class="reply-input" rows="2" placeholder="<%= placeholder %>"></textarea>' +
      '  <div class="reply-side">' +
      '    <select class="model-select" title="<%= modelSelectTitle %>" style="display:none;"></select>' +
      '    <input type="file" class="file-input" style="display:none;" ' +
      'accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.tif,.tiff,.pdf,.ai,.eps,.svg"/>' +
      '    <button class="btn btn-attach" title="<%= attachTitle %>">&#128206;</button>' +
      '    <button class="btn btn-send"><%= sendLabel %></button>' +
      '  </div>' +
      '</div>'
    ),

    events: {
      'click .btn-send': 'onSend',
      'keydown .reply-input': 'onReplyKeydown',
      'change .model-select': 'onModelChange',
      'click .btn-apply': 'onApply',
      'click .btn-reject': 'onReject',
      'click .btn-attach': 'onAttachClick',
      'change .file-input': 'onFileSelected',
      'click .msg-context-toggle': 'onToggleMessageContext',
      'click .attachment-original-img': 'onZoomOriginal',
      'click .img-zoom-overlay': 'onZoomClose'
    },

    initialize: function () {
      this.session = null;
      this.messages = [];
      this.lastRowId = null;
      this.sending = false;
      this.fetching = false;
      this.fetchingSince = 0;
      this.uploading = false;
      this.models = [];
      this.selectedModelCode = null;
      this.pollMs = null;
      this.thinkingTimer = null;
      this.thinkingSince = null;
    },

    serializeData: function () {
      var attachmentSession = this.options.attachmentSession;
      return {
        attachmentUrl: attachmentSession && attachmentSession.fileLink ?
          Settings.url('base', attachmentSession.fileLink) : null,
        attachmentName: attachmentSession ? (attachmentSession.fileName || '') : '',
        originalHint: _.i18n('orders.aiAssistant.originalFile', null,
          'Current file — the chat proposes modified versions below.'),
        openLabel: _.i18n('common.open', null, 'Open'),
        zoomTitle: _.i18n('orders.aiAssistant.zoom', null, 'Click to zoom'),
        placeholder: this.options.attachmentSession ?
          _.i18n('orders.aiAssistant.filePlaceholder', null,
            'Describe the modification to apply to this file…') :
          _.i18n('orders.aiAssistant.placeholder', null,
            'Ask for a change to the composition or the custom proofsheet…'),
        sendLabel: _.i18n('common.send', null, 'Send'),
        thinkingLabel: _.i18n('orders.aiAssistant.thinking', null, 'The assistant is working…'),
        assistantName: _.i18n('orders.aiAssistant.name', null, 'AI assistant'),
        modelSelectTitle: _.i18n('orders.aiAssistant.modelSelect', null, 'AI model to use'),
        attachTitle: _.i18n('orders.aiAssistant.attach', null, 'Send a file to the assistant')
      };
    },

    getPoi: function () {
      if (this.model && this.model.get('poi')) { return this.model.get('poi'); }
      if (this.model && this.model.get('order')) { return this.model.get('order').get('purchaseOrderId'); }
      return null;
    },

    isOrderValidated: function () {
      var order = this.model ? this.model.get('order') : null;
      if (order && typeof order.get === 'function') { return !!order.get('validated'); }
      return false;
    },

    // Lightbox zoom of the original file: click the thumbnail to fill the
    // screen, click anywhere (or Esc) to close.
    onZoomOriginal: function (e) {
      var $overlay = this.$('.img-zoom-overlay');
      $overlay.find('img').attr('src', $(e.currentTarget).attr('src'));
      $overlay.show();
    },

    onZoomClose: function () {
      this.$('.img-zoom-overlay').hide();
    },

    onShow: function () {
      // Non-image files whose preview servlet can't render: drop the broken
      // image but keep the name bar.
      this.$('.attachment-original-img').on('error', function () {
        $(this).hide();
      });
      var self = this;
      $(document).on('keydown.aiChatZoom' + this.cid, function (e) {
        if (e.which === 27) { self.onZoomClose(); }
      });
      this.openSession();
      this.loadModels();
      this.subscribeToNotifications();
      this.startPolling();
      // Background tabs get their timers throttled hard (up to ~1/min in
      // Chrome), so coming back to the tab could show a stale thread for up
      // to a minute — refetch the moment the tab becomes visible again.
      $(document).on('visibilitychange.aiChat' + this.cid, function () {
        if (!document.hidden && !self.isDestroyed) {
          self.fetchNewMessages();
        }
      });
    },

    loadModels: function () {
      var self = this;
      App.request('aiChat:models').done(function (models) {
        if (self.isDestroyed) { return; }
        self.models = models || [];
        self.renderModelOptions();
      });
    },

    // The selector only appears when there is an actual choice; with zero or
    // one configured model there is nothing to pick.
    renderModelOptions: function () {
      var $select = this.$('.model-select');
      if (!$select.length) { return; }
      if (this.models.length < 2) {
        $select.hide();
        return;
      }
      // Priority: choice made in this view > the user's LATEST selection
      // anywhere (localStorage) > the session's stored model > first entry.
      // The remembered code is sent with the next message, which also
      // switches the session server-side.
      var selected = this.selectedModelCode;
      if (!selected) { selected = storedModelCode(); }
      if (!selected && this.session) { selected = this.session.modelCode; }
      if (!selected) { selected = this.models[0].code; }
      var html = _.map(this.models, function (model) {
        return '<option value="' + escapeAttr(model.code) + '">' +
          escapeHtml(model.label || model.code) +
          '</option>';
      }).join('');
      $select.html(html);
      $select.val(selected);
      // val() silently no-ops on an unknown code (e.g. the session's stored
      // model was removed) — fall back to the first entry.
      if (!$select.val()) { $select.val(this.models[0].code); }
      this.selectedModelCode = $select.val();
      $select.show();
    },

    onModelChange: function () {
      // Applied server-side with the NEXT message — the session keeps its
      // model until the user actually sends something.
      this.selectedModelCode = this.$('.model-select').val();
      if (this.selectedModelCode) { storeModelCode(this.selectedModelCode); }
    },

    onDestroy: function () {
      $(document).off('visibilitychange.aiChat' + this.cid);
      $(document).off('keydown.aiChatZoom' + this.cid);
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = null;
      }
    },

    openSession: function () {
      var self = this;
      var onOpened = function (data) {
        if (self.isDestroyed) { return; }
        self.applySessionData(data, false);
      };
      var onFailed = function () {
        if (self.isDestroyed) { return; }
        self.$('.msg-list').html('<li class="empty">' +
          escapeHtml(_.i18n('orders.aiAssistant.loadError', null, 'Could not open the AI assistant.')) +
          '</li>');
      };
      // File-editing popup: the session is scoped to one attachment, not to
      // the order chat.
      if (this.options.attachmentSession) {
        App.request('aiChat:open-attachment-session',
          this.options.attachmentSession.poi,
          this.options.attachmentSession.fileUid).done(onOpened).fail(onFailed);
        return;
      }
      var poi = this.getPoi();
      if (!poi) { return; }
      App.request('aiChat:open-session', poi).done(onOpened).fail(onFailed);
    },

    // WS is best-effort; an incremental poll keeps the thread correct even
    // with the socket down. While a turn runs the poll tightens to 4s so the
    // working indicator and the reply appear promptly without WS.
    startPolling: function () {
      this.setPollSpeed(15000);
    },

    setPollSpeed: function (ms) {
      if (this.pollMs === ms) { return; }
      this.pollMs = ms;
      if (this.pollTimer) { clearInterval(this.pollTimer); }
      var self = this;
      this.pollTimer = setInterval(function () {
        if (self.isDestroyed || !self.session) { return; }
        self.fetchNewMessages();
      }, ms);
    },

    // Guarded against overlap: the WS notification and the tightened poll
    // can fire together when a turn ends — two in-flight requests with the
    // same sinceId would each append the same messages. The guard EXPIRES
    // after 25s (the ajax timeout is 20s): a hung request must never kill
    // the poll loop for good — that freezes the whole chat while the agent
    // keeps working server-side.
    fetchNewMessages: function () {
      var self = this;
      if (!this.session) { return; }
      if (this.fetching && Date.now() - this.fetchingSince < 25000) { return; }
      this.fetching = true;
      this.fetchingSince = Date.now();
      App.request('aiChat:messages', this.session.secId, this.lastRowId).done(function (data) {
        self.fetching = false;
        if (self.isDestroyed) { return; }
        self.applySessionData(data, true);
      }).fail(function () {
        self.fetching = false;
      });
    },

    applySessionData: function (data, incremental) {
      var d = data || {};
      this.session = { secId: d.secId, status: d.status, modelCode: d.modelCode, processing: d.processing };
      var newMessages = d.messages || [];
      if (incremental) {
        // Belt and braces on top of the fetch guard: never append a rowId
        // that is already in the thread.
        var knownRowIds = {};
        _.each(this.messages, function (msg) {
          if (msg.rowId) { knownRowIds[msg.rowId] = true; }
        });
        newMessages = _.filter(newMessages, function (msg) {
          return !msg.rowId || !knownRowIds[msg.rowId];
        });
        this.messages = this.messages.concat(newMessages);
      } else {
        this.messages = newMessages;
      }
      var last = _.last(this.messages);
      if (last && last.rowId) { this.lastRowId = last.rowId; }
      this.renderMessages();
      this.renderModelOptions();
      if (d.processing && d.processingStartedAt && !this.thinkingSince) {
        // reopening the tab mid-turn: elapsed counts from the real start
        this.thinkingSince = new Date(d.processingStartedAt).getTime();
      }
      this.setThinking(!!d.processing);
      if (d.processing) { this.setThinkingStatus(d.processingStatus); }
      if (newMessages.length) { this.scrollToBottom(); }
    },

    renderMessages: function () {
      var $list = this.$('.msg-list');
      if (!$list.length) { return; }
      var orderValidated = this.isOrderValidated();
      var html = _.map(this.messages, function (msg) {
        return renderMessage(msg, orderValidated);
      }).join('');
      $list.html(html || '<li class="empty">' +
        escapeHtml(_.i18n('orders.aiAssistant.empty', null,
          'Ask the assistant to modify a logo or the custom proofsheet.')) +
        '</li>');
    },

    setThinking: function (on) {
      this.$('.thinking').toggle(!!on);
      this.$('.btn-send').prop('disabled', !!on);
      if (on) {
        if (!this.thinkingSince) { this.thinkingSince = Date.now(); }
        if (!this.thinkingTimer) {
          var self = this;
          this.thinkingTimer = setInterval(function () {
            if (self.isDestroyed) { return; }
            var seconds = Math.round((Date.now() - self.thinkingSince) / 1000);
            self.$('.thinking-elapsed').text(seconds + 's');
          }, 1000);
        }
        this.scrollToBottom();
      } else {
        if (this.thinkingTimer) {
          clearInterval(this.thinkingTimer);
          this.thinkingTimer = null;
        }
        this.thinkingSince = null;
        this.$('.thinking-elapsed').text('');
      }
      this.setPollSpeed(on ? 4000 : 15000);
    },

    setThinkingStatus: function (text) {
      if (text) { this.$('.thinking-status').text(text); }
    },

    scrollToBottom: function () {
      var $panel = this.$('.thread-panel');
      if ($panel.length) { $panel.scrollTop($panel[0].scrollHeight); }
    },

    // Exclude/restore an exchange from the AI context. The server flips the
    // whole exchange (question + replies) and answers the full session json
    // — re-render everything from it rather than patching locally.
    onToggleMessageContext: function (e) {
      var self = this;
      var $button = $(e.currentTarget);
      if (!this.session || $button.prop('disabled')) { return; }
      var messageSecId = $button.attr('data-message-sec-id');
      var currentlyIncluded = $button.attr('data-included') === '1';
      if (!messageSecId) { return; }
      $button.prop('disabled', true);
      App.request('aiChat:set-message-context', this.session.secId, messageSecId, !currentlyIncluded)
        .done(function (data) {
          if (self.isDestroyed) { return; }
          self.applySessionData(data, false);
        })
        .fail(function () {
          if (self.isDestroyed) { return; }
          $button.prop('disabled', false);
        });
    },

    onAttachClick: function () {
      if (this.uploading) { return; }
      this.$('.file-input').trigger('click');
    },

    onFileSelected: function () {
      var self = this;
      var input = this.$('.file-input')[0];
      if (!input || !input.files || !input.files.length || !this.session || this.uploading) { return; }
      var file = input.files[0];
      input.value = '';
      if (file.size > 15 * 1024 * 1024) {
        window.alert(_.i18n('orders.aiAssistant.fileTooBig', null, 'File too big (max 15 MB).'));
        return;
      }
      this.uploading = true;
      this.$('.btn-attach').prop('disabled', true);
      App.request('aiChat:upload-file', this.session.secId, file).done(function (message) {
        if (self.isDestroyed) { return; }
        self.uploading = false;
        self.$('.btn-attach').prop('disabled', false);
        self.messages.push(message);
        if (message && message.rowId) { self.lastRowId = message.rowId; }
        self.renderMessages();
        self.scrollToBottom();
        self.$('.reply-input').focus();
      }).fail(function (err) {
        if (self.isDestroyed) { return; }
        self.uploading = false;
        self.$('.btn-attach').prop('disabled', false);
        if (err && err.unsupported) {
          window.alert(_.i18n('orders.aiAssistant.fileUnsupported', null,
            'This file type is not supported (images, PDF, AI, EPS, SVG).'));
        } else {
          window.alert(_.i18n('orders.aiAssistant.fileUploadError', null,
            'Could not upload the file. Please try again.'));
        }
      });
    },

    onReplyKeydown: function (e) {
      if (e.which === 13 && !e.shiftKey) {
        e.preventDefault();
        this.onSend();
      }
    },

    onSend: function () {
      var self = this;
      if (this.sending || !this.session) { return; }
      var $input = this.$('.reply-input');
      var text = ($input.val() || '').trim();
      if (!text) { return; }
      this.sending = true;
      App.request('aiChat:send-message', this.session.secId, text, this.selectedModelCode).done(function (message) {
        if (self.isDestroyed) { return; }
        self.sending = false;
        $input.val('');
        self.messages.push(message);
        if (message && message.rowId) { self.lastRowId = message.rowId; }
        self.renderMessages();
        self.setThinking(true);
        self.scrollToBottom();
        $input.focus();
      }).fail(function (err) {
        if (self.isDestroyed) { return; }
        self.sending = false;
        if (err && err.busy) {
          self.setThinking(true);
        } else {
          window.alert(_.i18n('orders.aiAssistant.sendError', null,
            'Could not send the message. Please try again.'));
        }
      });
    },

    // Busy state of one proposal card while its apply/reject request runs:
    // buttons locked (kills double clicks) and the Apply label swapped for a
    // progress one. Re-rendering the thread naturally clears it.
    setCardBusy: function ($card, busy) {
      $card.toggleClass('busy', !!busy);
      $card.find('.btn-apply, .btn-reject').prop('disabled', !!busy);
      var label = busy ?
        _.i18n('orders.aiAssistant.applying', null, 'Applying…') :
        _.i18n('orders.aiAssistant.apply', null, 'Apply');
      $card.find('.btn-apply').text(label);
    },

    onApply: function (e) {
      var self = this;
      var $card = $(e.currentTarget).closest('.ai-job-card');
      var actionSecId = $card.attr('data-action-sec-id');
      if (actionSecId) {
        this.onApplyAction(actionSecId, $card);
        return;
      }
      var jobSecId = $card.attr('data-job-sec-id');
      if (!jobSecId) { return; }
      if (!window.confirm(_.i18n('orders.aiAssistant.applyConfirm', null,
          'Apply this modification to the order?'))) { return; }
      this.setCardBusy($card, true);
      App.request('aiChat:apply-edit', jobSecId).done(function (job) {
        if (self.isDestroyed) { return; }
        self.updateJobInMessages(job);
        self.renderMessages();
        // The composition/proofsheet panels listen on these events and
        // re-fetch — same channel the Compuzz Pro desktop app uses.
        var cpu = CompuzzProUtils.getInstance();
        if (job && job.targetType === 'PROOFSHEET') {
          cpu.trigger(CompuzzProUtils.events.COMPUZZ_PRO_REFRESH_CUSTOM_PROOFSHEET, {});
        } else {
          cpu.trigger(CompuzzProUtils.events.COMPUZZ_PRO_REFRESH_COMPOSITION, {});
        }
      }).fail(function (err) {
        if (self.isDestroyed) { return; }
        if (err && err.conflict) {
          window.alert(err.message ||
            (_.i18n('orders.aiAssistant.applyConflict', null,
              'This proposal can no longer be applied — it is outdated or the order is locked.')));
          self.refreshJobs();
        } else {
          window.alert(_.i18n('orders.aiAssistant.applyError', null, 'Could not apply the modification.'));
          self.renderMessages(); // restore the card's buttons
        }
      });
    },

    // Executes the proposed API call with the APPROVER's token — the order
    // page itself may show stale data afterwards (the call can change
    // anything), so the user gets the outcome on the card and the usual
    // page refresh flows take care of the rest.
    onApplyAction: function (actionSecId, $card) {
      var self = this;
      if (!window.confirm(_.i18n('orders.aiAssistant.applyActionConfirm', null,
          'Execute this action?'))) { return; }
      this.setCardBusy($card, true);
      App.request('aiChat:apply-action', actionSecId).done(function (job) {
        if (self.isDestroyed) { return; }
        self.updateActionJobInMessages(job);
        self.renderMessages();
        if (job && job.status === 'PROPOSED' && job.errorMessage) {
          // The backend feeds the failure back to the assistant and starts a
          // corrective turn — surface the system message and the thinking
          // indicator right away instead of waiting for the next poll.
          self.setThinking(true);
          self.fetchNewMessages();
        }
      }).fail(function (err) {
        if (self.isDestroyed) { return; }
        if (err && err.conflict) {
          window.alert(err.message ||
            (_.i18n('orders.aiAssistant.applyConflict', null,
              'This proposal can no longer be applied — it is outdated or the order is locked.')));
          self.refreshJobs();
        } else {
          window.alert(_.i18n('orders.aiAssistant.applyActionError', null, 'Could not execute the action.'));
          self.renderMessages(); // restore the card's buttons
        }
      });
    },

    onReject: function (e) {
      var self = this;
      var $card = $(e.currentTarget).closest('.ai-job-card');
      var actionSecId = $card.attr('data-action-sec-id');
      if (actionSecId) {
        this.setCardBusy($card, true);
        App.request('aiChat:reject-action', actionSecId).done(function (job) {
          if (self.isDestroyed) { return; }
          self.updateActionJobInMessages(job);
          self.renderMessages();
        }).fail(function () {
          if (self.isDestroyed) { return; }
          self.renderMessages();
        });
        return;
      }
      var jobSecId = $card.attr('data-job-sec-id');
      if (!jobSecId) { return; }
      this.setCardBusy($card, true);
      App.request('aiChat:reject-edit', jobSecId).done(function (job) {
        if (self.isDestroyed) { return; }
        self.updateJobInMessages(job);
        self.renderMessages();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.renderMessages();
      });
    },

    // Patch the decided job into the local message list (jobs are embedded
    // in their assistant message's editJobs array).
    updateJobInMessages: function (job) {
      if (!job) { return; }
      _.each(this.messages, function (msg) {
        _.each(msg.editJobs || [], function (existing, idx) {
          if (existing.secId === job.secId) {
            msg.editJobs[idx] = job;
          }
        });
      });
    },

    updateActionJobInMessages: function (job) {
      if (!job) { return; }
      _.each(this.messages, function (msg) {
        _.each(msg.actionJobs || [], function (existing, idx) {
          if (existing.secId === job.secId) {
            msg.actionJobs[idx] = job;
          }
        });
      });
    },

    // Full job re-sync after a conflict (another user decided, or the target
    // changed underneath the proposal).
    refreshJobs: function () {
      var self = this;
      if (!this.session) { return; }
      App.request('aiChat:jobs', this.session.secId).done(function (jobs) {
        if (self.isDestroyed) { return; }
        var bySecId = {};
        _.each(jobs || [], function (job) { bySecId[job.secId] = job; });
        _.each(self.messages, function (msg) {
          _.each(msg.editJobs || [], function (existing, idx) {
            if (bySecId[existing.secId]) {
              msg.editJobs[idx] = bySecId[existing.secId];
            }
          });
        });
        self.renderMessages();
      });
      App.request('aiChat:action-jobs', this.session.secId).done(function (jobs) {
        if (self.isDestroyed) { return; }
        var bySecId = {};
        _.each(jobs || [], function (job) { bySecId[job.secId] = job; });
        _.each(self.messages, function (msg) {
          _.each(msg.actionJobs || [], function (existing, idx) {
            if (bySecId[existing.secId]) {
              msg.actionJobs[idx] = bySecId[existing.secId];
            }
          });
        });
        self.renderMessages();
      });
    },

    subscribeToNotifications: function () {
      var self = this;
      var nc = NotificationCenter.getInstance();

      function isOurs(notification) {
        var inner = notification && notification.content && notification.content.content;
        return inner && self.session && inner.entitySecId === self.session.secId;
      }

      nc.addListener(this, NotificationCenter.events.AI_CHAT_MESSAGE_ADDED, function (n) {
        if (self.isDestroyed || !isOurs(n)) { return; }
        // Refetch incrementally — the payload lacks derived fields and the
        // poll path already handles merge + cursor + thinking state.
        self.fetchNewMessages();
      });
      nc.addListener(this, NotificationCenter.events.AI_CHAT_JOB_STATUS, function (n) {
        if (self.isDestroyed || !isOurs(n)) { return; }
        self.refreshJobs();
      });
      // Live progress of the running turn ("Editing the image…") — instant
      // indicator updates; the 4s poll is the fallback when WS is down.
      nc.addListener(this, NotificationCenter.events.AI_CHAT_TURN_PROGRESS, function (n) {
        if (self.isDestroyed || !isOurs(n)) { return; }
        var inner = n.content.content;
        self.setThinking(true);
        self.setThinkingStatus(inner.text);
      });
    }
  });
});

